import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Bot } from 'lucide-react';
import { RxCross2 } from 'react-icons/rx';
import { useReactToPrint } from 'react-to-print';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import { useParams } from 'react-router-dom';
import sessionsService from '../../services/sessionsServices';
import teamService from '../../services/teamService';
import { AuthContext } from '../../Context/authContext';
import { useContextHook } from 'use-context-hook';
import SourcesTab from './Sources';
import 'swiper/css';
import 'swiper/css/navigation';
import { ChatSectionStyled, ChatColumn, SearchResultCol } from './ChatSection.styles';
import KaiChatHeader, { DEFAULT_KAI_ID } from './KaiChatHeader';
import KaiChatInput from './KaiChatInput';
import ChatMessageItem from './ChatMessageItem';

function getAgentIconSrc(agent) {
  if (!agent?.icon) return null;
  if (agent.icon.startsWith('http://') || agent.icon.startsWith('https://')) {
    return agent.icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : agent.icon;
  }
  if (agent.icon.startsWith('data:')) return agent.icon;
  return `data:image/png;base64,${agent.icon}`;
}

const ChatSection = () => {
  const printRef = useRef(null);
  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const [activeTab, setActiveTab] = useState('Sources');
  const {
    selectedAgent,
    chatMessages,
    setChatmessages,
    setSessionAgent,
    sessionAgent,
    isActiveRef,
    setSessionID,
    updateAgent,
  } = useContext(TemporaryChatContext);
  const [selectedChatSources, setSelectedChatSources] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  const { id } = useParams();
  const tabs = ['Sources'];
  const hasSession = Boolean(id);
  const isTeamChat = selectedAgent?.mode === 'team' || sessionAgent?.mode === 'team';

  useEffect(() => {
    const fetchTeamData = async () => {
      const teamId =
        selectedAgent?.mode === 'team'
          ? selectedAgent?.value
          : sessionAgent?.mode === 'team'
            ? sessionAgent?.value
            : null;
      if (!teamId) {
        setTeamDetails(null);
        return;
      }
      try {
        const res = await teamService?.getTeamDetails(teamId);
        setTeamDetails(res?.detail);
      } catch (error) {
        console.error('Failed to fetch team details:', error);
      }
    };

    fetchTeamData();
  }, [selectedAgent, sessionAgent]);

  const getSessions = async sessionId => {
    try {
      const aiResp = await sessionsService.getSessionHistory({
        user_id: user?.data?.ID,
        platform_id: 'gramielabs',
        agent_id: selectedAgent?.value || sessionAgent?.value || DEFAULT_KAI_ID,
        session_id: sessionId,
      });
      if (aiResp?.agent_name || aiResp?.agent_id) {
        const historyMode = aiResp?.mode || (aiResp?.session_mode === 'team' ? 'team' : 'agent');
        const historyAgent = {
          label: aiResp?.agent_name,
          value: aiResp?.agent_id,
          icon: aiResp?.icon,
          mode: historyMode,
        };

        setSessionAgent(prev => {
          if (prev?.mode === 'team') {
            return {
              ...prev,
              label: prev.label || historyAgent.label,
              icon: prev.icon || historyAgent.icon,
              mode: 'team',
              value: prev.value || historyAgent.value,
            };
          }
          return historyAgent;
        });

        const keepExistingTeam = historyMode === 'team' && selectedAgent?.mode === 'team';
        if (!keepExistingTeam && String(selectedAgent?.value) !== String(historyAgent.value)) {
          updateAgent(historyAgent);
        }
      }

      const formattedMessages = aiResp?.history?.flatMap(item => {
        const isSource = item?.sources ? item?.sources : [];
        const websearchSources = isSource?.filter(src => src.type === 'google_search');
        const fileSources = isSource?.filter(src => src.type === 'file');
        return [
          {
            human: item.human,
            ai: item?.ai,
            isStreaming: false,
            intermediate_steps: [
              {
                tool_name: 'thinking...',
                content: item.human,
              },
              ...(item?.intermediate_steps || []),
            ],
            source: websearchSources.length > 0 ? websearchSources : [],
            files: fileSources.length > 0 ? fileSources : [],
            images: item?.images,
            isLoading: false,
          },
        ];
      });

      setChatmessages(formattedMessages);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id && isActiveRef.current) {
      getSessions(id);
      setSessionID(id);
    }
    if (!id) {
      setChatmessages([]);
      setSessionID(null);
    }
  }, [id]);

  const displayAgent = selectedAgent?.mode === 'team' ? selectedAgent : selectedAgent?.label ? selectedAgent : sessionAgent;
  const emptyStateIcon = useMemo(() => getAgentIconSrc(displayAgent), [displayAgent]);

  const sessionAgentIcon = useMemo(() => {
    if (selectedAgent?.mode === 'team' || sessionAgent?.mode === 'team') {
      return getAgentIconSrc(selectedAgent?.mode === 'team' ? selectedAgent : sessionAgent);
    }
    return getAgentIconSrc(sessionAgent);
  }, [selectedAgent, sessionAgent]);

  const lastMessageIndex = chatMessages.length - 1;
  const hasMessages = chatMessages.length > 0;
  const exportInProgress = chatMessages.some(message => message?.isStreaming || message?.isLoading);
  const exportDate = new Date().toLocaleString();
  const exportUserEmail = user?.email || user?.data?.email;

  const printDocumentTitle = () => {
    const agentName = (displayAgent?.label || 'Kai')
      .replace(/[\\/:*?"<>|]+/g, '')
      .trim()
      .replace(/\s+/g, ' ');
    const date = new Date().toISOString().slice(0, 10);
    return id ? `${agentName}_${id}_${date}` : `${agentName}_${date}`;
  };

  const handleExportPdf = useReactToPrint({
    contentRef: printRef,
    documentTitle: printDocumentTitle,
    pageStyle: `
      @page {
        size: auto;
        margin: 0;
      }

      body {
        margin: 0;
        background: #fff;
        color: #111;
      }
    `,
  });

  return (
    <ChatSectionStyled className={selectedChatSources !== null ? 'sources-active' : ''}>
      <ChatColumn ref={printRef} className="kai-chat-layout">
        <KaiChatHeader onExportPdf={handleExportPdf} exportDisabled={!hasMessages || exportInProgress} />
        <header className="kai-chat-print-header" aria-hidden="true">
          <h1>{displayAgent?.label || 'Kai'} Chat</h1>
          <dl className="kai-chat-print-meta">
            <dt>Exported</dt>
            <dd>{exportDate}</dd>
            {exportUserEmail && (
              <>
                <dt>User</dt>
                <dd>{exportUserEmail}</dd>
              </>
            )}
            {id && (
              <>
                <dt>Session ID</dt>
                <dd>{id}</dd>
              </>
            )}
          </dl>
        </header>

        <div className="text-block kai-chat-messages">
          {!hasMessages ? (
            <div className="kai-chat-empty">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 overflow-hidden">
                {!displayAgent?.icon && !emptyStateIcon ? (
                  <div className="w-full h-full animate-pulse bg-[#262626]" aria-hidden="true" />
                ) : emptyStateIcon ? (
                  <img src={emptyStateIcon} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Bot className="w-8 h-8 text-primary" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-white mb-2 font-serif">
                {displayAgent?.label || 'Kai Chat'}
              </h3>
              <p className="text-sm text-[#808080] max-w-md text-center">
                {isTeamChat
                  ? hasSession
                    ? 'Team chat is ready. Send a message to collaborate with this team.'
                    : `Click New Chat to start a session with ${displayAgent?.label || 'this team'}.`
                  : hasSession
                    ? 'Your conversational interface for agents and copilots. Send a message to begin.'
                    : displayAgent?.label
                      ? `Click New Chat to start a session with ${displayAgent.label}.`
                      : 'Select an agent, then click New Chat to create a session before messaging.'}
              </p>
            </div>
          ) : (
            <div className="kai-chat-thread">
              {chatMessages?.map((elem, ind) => (
                <ChatMessageItem
                  key={`${id}-${ind}-${elem.human?.slice(0, 20) ?? ''}`}
                  elem={elem}
                  ind={ind}
                  user={user}
                  selectedAgent={
                    selectedAgent?.mode === 'team'
                      ? selectedAgent
                      : sessionAgent?.mode === 'team'
                        ? sessionAgent
                        : selectedAgent
                  }
                  teamDetails={teamDetails}
                  sessionAgentIcon={sessionAgentIcon}
                  selectedChatSources={selectedChatSources}
                  onSelectSources={setSelectedChatSources}
                  isLast={ind === lastMessageIndex}
                />
              ))}
            </div>
          )}
        </div>

        <div className="kai-chat-input">
          <div className="kai-chat-search">
            <KaiChatInput sessionId={id} createSession={false} />
          </div>
        </div>
      </ChatColumn>

      {selectedChatSources !== null && (
        <SearchResultCol>
          <div>
            <ul className="tablist">
              {tabs.map((tab, index) => (
                <li key={tab}>
                  <button
                    key={index}
                    type="button"
                    className={tab === activeTab ? 'active' : ''}
                    onClick={() => setActiveTab(tab)}>
                    {tab}
                  </button>
                </li>
              ))}
              <button type="button" className="source-close" title="close" onClick={() => setSelectedChatSources(null)}>
                <RxCross2 color="var(--danger)" />
              </button>
            </ul>
            <div className="tab-content">
              {activeTab === 'Sources' && <SourcesTab data={chatMessages[selectedChatSources]} />}
            </div>
          </div>
        </SearchResultCol>
      )}
    </ChatSectionStyled>
  );
};

export default ChatSection;
